// MF-577 — The curator chooses which Chart Library items the school offers to the people who build Boards.
// A Board that already uses an item you switch off keeps working: it only stops being offered for new tiles.
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
  Typography
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { getLibrary, saveLibraryCuration } from '../../../../../services/boards/boards.service'
import { SUMMARY_PALETTE } from '../boardTheme'

const LibraryAvailabilityDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [chosen, setChosen] = useState(new Set())
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setItems(null)
    setError(null)
    getLibrary()
      .then((lib) => {
        const list = (lib?.items || []).filter((i) => i.status !== 'retired')
        setItems(list)
        setChosen(new Set(list.filter((i) => i.offered).map((i) => i.code)))
      })
      .catch((err) => setError(err?.message || 'The Library could not be loaded'))
  }, [open])

  const groups = useMemo(() => {
    const text = q.trim().toLowerCase()
    const shown = (items || []).filter(
      (i) =>
        !text ||
        `${i.title} ${i.group} ${i.description} ${(i.tags || []).join(' ')}`.toLowerCase().includes(text)
    )
    const by = new Map()
    for (const item of shown) by.set(item.group, [...(by.get(item.group) || []), item])
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [items, q])

  const shownCodes = groups.flatMap(([, list]) => list.map((i) => i.code))
  const searching = q.trim() !== ''

  const setMany = (codes, on) =>
    setChosen((cur) => {
      const next = new Set(cur)
      codes.forEach((c) => (on ? next.add(c) : next.delete(c)))
      return next
    })

  const save = async (allowed) => {
    setBusy(true)
    try {
      await saveLibraryCuration(allowed)
      enqueueSnackbar(allowed === null ? 'Back to the default: everything that is not sensitive' : 'Saved', {
        variant: 'success'
      })
      onClose(true)
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not save', { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth='lg' fullWidth>
      <DialogTitle>Library elements offered to builders</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, mb: 1.5 }}>
          People who build Boards only see the elements you tick here (you and Maestro always see all of
          them). A Board that already uses an element keeps it if you untick it: it just stops being offered
          for new tiles. Sensitive elements (discipline, medical, learning support) are not offered until you
          tick them.
        </Typography>
        {error ? <Alert severity='error'>{error}</Alert> : null}
        {items === null && !error ? (
          [0, 1, 2, 3].map((i) => <Skeleton key={i} height={40} />)
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size='small' placeholder='Search' value={q} onChange={(e) => setQ(e.target.value)} />
              <Button size='small' onClick={() => setMany(shownCodes, true)}>
                {searching ? 'Tick the results' : 'Tick all'}
              </Button>
              <Button size='small' onClick={() => setMany(shownCodes, false)}>
                {searching ? 'Untick the results' : 'Untick all'}
              </Button>
              <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>
                {chosen.size} of {(items || []).length} offered
              </Typography>
            </Box>
            {groups.map(([group, list]) => {
              const on = list.filter((i) => chosen.has(i.code)).length
              return (
                <Box key={group} sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      pb: 0.5,
                      borderBottom: `1px solid ${SUMMARY_PALETTE.grid}`
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{group}</Typography>
                    <Chip size='small' label={`${on} of ${list.length}`} sx={{ height: 20, fontSize: 11 }} />
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size='small'
                      onClick={() =>
                        setMany(
                          list.map((i) => i.code),
                          true
                        )
                      }
                    >
                      All
                    </Button>
                    <Button
                      size='small'
                      onClick={() =>
                        setMany(
                          list.map((i) => i.code),
                          false
                        )
                      }
                    >
                      None
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.25,
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
                    }}
                  >
                    {list.map((item) => {
                      const checked = chosen.has(item.code)
                      return (
                        <Box
                          key={item.code}
                          role='button'
                          tabIndex={0}
                          onClick={() => setMany([item.code], !checked)}
                          onKeyDown={(e) =>
                            (e.key === ' ' || e.key === 'Enter') &&
                            (e.preventDefault(), setMany([item.code], !checked))
                          }
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                            p: 1.25,
                            cursor: 'pointer',
                            borderRadius: '10px',
                            bgcolor: '#fff',
                            border: `1.5px solid ${checked ? '#2A69A7' : SUMMARY_PALETTE.grid}`,
                            boxShadow: checked ? '0 0 0 2px rgba(42,105,167,.12)' : 'none',
                            '&:hover': { borderColor: '#2A69A7' }
                          }}
                        >
                          <Checkbox size='small' checked={checked} tabIndex={-1} sx={{ p: 0.25 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item.title}</Typography>
                              <Chip
                                size='small'
                                variant='outlined'
                                label={item.kind === 'card' ? 'card' : 'chart'}
                                sx={{ height: 18, fontSize: 10 }}
                              />
                              {item.sensitive ? (
                                <Chip
                                  size='small'
                                  color='warning'
                                  label='sensitive'
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                              ) : null}
                            </Box>
                            <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 0.25 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )
            })}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => save(null)}
          disabled={busy || items === null}
          sx={{ mr: 'auto', textTransform: 'none' }}
        >
          Back to the default
        </Button>
        <Button onClick={() => onClose(false)} disabled={busy}>
          Cancel
        </Button>
        <Button variant='contained' onClick={() => save([...chosen])} disabled={busy || items === null}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LibraryAvailabilityDialog
